import { computed, Injectable, signal } from '@angular/core';
import _ from 'lodash';
import { Scope } from '../../core/models';

@Injectable({
  providedIn: 'root',
})
export class LoadingBarrierService {
  private readonly registered = signal(new Map<Scope, Set<string>>());
  private readonly ready = signal(new Map<Scope, Set<string>>());

  register(scope: Scope, key: string): void {
    const reg = _.cloneDeep(this.registered());
    const set = reg.get(scope) ?? new Set<string>();
    set.add(key);
    reg.set(scope, set);

    this.registered.set(reg);
  }

  markReady(scope: Scope, key: string): void {
    const reg = this.registered();
    if (!reg.get(scope)?.has(key)) return;
    const ok = _.cloneDeep(this.ready());
    const set = ok.get(scope) ?? new Set<string>();
    set.add(key);
    ok.set(scope, set);
    this.ready.set(ok);
  }

  resetScope(scope: Scope): void {
    //console.log("resetting.")
    const reg = _.cloneDeep(this.registered());
    const ok = _.cloneDeep(this.ready());
    reg.delete(scope);
    ok.delete(scope);
    this.registered.set(reg);
    this.ready.set(ok);
    //console.log('RESET', this.ready(), this.registered());
  }

  isScopeReady = (scope: Scope) =>
    computed(() => {
      const reg = this.registered().get(scope);
      if (!reg || reg.size === 0) return false;
      const ok = this.ready().get(scope) ?? new Set();
      const result = reg.size === ok.size;
      return result;
    });

  scopePending = (scope: Scope) =>
    computed(() => {
      const reg = this.registered().get(scope)?.size ?? 0;
      const ok = this.ready().get(scope)?.size ?? 0;
      return Math.max(reg - ok, 0);
    });

  isScopeBusy = (scope: Scope) =>
    computed(() => {
      const reg = this.registered().get(scope)?.size ?? 0;
      const ok = this.ready().get(scope)?.size ?? 0;
      const result = reg > 0 && ok < reg; // busy only when there is work registered and not all done
      return result;
    });

  progressForScope = (scope: Scope) =>
    computed(() => {
      const reg = this.registered().get(scope)?.size ?? 0;
      const ok = this.ready().get(scope)?.size ?? 0;
      // while idle (reg===0) report 0; the overlay will be hidden so no flicker
      return reg > 0 ? Math.round((ok / reg) * 100) : 0;
    });
}
